import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, Trash2, Download, File as FileIcon, Image as ImageIcon } from "lucide-react";
import { fmtDate } from "@/lib/formatters";

function UploadCard({ docTypes, onUpload, uploading }) {
  const fileRef = useRef(null);
  const [type, setType] = useState("rg");
  const [notes, setNotes] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    await onUpload({ file, type, notes });
    if (fileRef.current) fileRef.current.value = "";
    setNotes("");
  }

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-3">
          <Upload className="h-4 w-4 text-blue-700" />
          <h3 className="font-display font-semibold text-slate-900">Enviar novo documento</h3>
        </div>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div className="space-y-1.5">
            <Label>Tipo</Label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full border border-slate-300 rounded-md px-3 h-11 md:h-9 text-base md:text-sm bg-white min-h-[44px]"
              data-testid="select-attach-type"
            >
              {docTypes.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
          </div>
          <div className="md:col-span-2 space-y-1.5">
            <Label>Observação (opcional)</Label>
            <Input type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Ex: RG atualizado em 2024" data-testid="input-attach-notes" />
          </div>
          <div className="space-y-1.5">
            <Label>Arquivo (PDF/imagem até 25MB)</Label>
            <Input ref={fileRef} type="file" accept=".pdf,image/*,.heic,.webp" data-testid="input-attach-file" />
          </div>
          <div className="md:col-span-4 flex justify-end">
            <Button type="submit" disabled={uploading} className="bg-blue-700 hover:bg-blue-800 h-11 md:h-9 w-full sm:w-auto" data-testid="btn-attach-upload">
              <Upload className="h-4 w-4 mr-2" /> {uploading ? "Enviando..." : "Enviar"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function AttachmentsTable({ attachments, onDownload, onDelete }) {
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="p-0 overflow-x-auto">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h3 className="font-display font-semibold text-slate-900">Documentos do paciente</h3>
          <div className="text-xs text-slate-500">{attachments.length} arquivo(s)</div>
        </div>
        {attachments.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">Nenhum documento pessoal enviado ainda.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-4 py-2.5 w-8"></th>
                <th className="text-left px-4 py-2.5">Tipo</th>
                <th className="text-left px-4 py-2.5">Arquivo</th>
                <th className="text-left px-4 py-2.5">Observação</th>
                <th className="text-left px-4 py-2.5">Enviado</th>
                <th className="text-right px-4 py-2.5">Ações</th>
              </tr>
            </thead>
            <tbody>
              {attachments.map((a, idx) => {
                const isImage = (a.content_type || "").startsWith("image/");
                return (
                  <tr key={a.id} className="border-t border-slate-100" data-testid={`attach-row-${idx}`}>
                    <td className="px-4 py-2.5">
                      {isImage ? <ImageIcon className="h-4 w-4 text-slate-500" /> : <FileIcon className="h-4 w-4 text-slate-500" />}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                        {a.label || a.doc_type}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 font-medium text-slate-900 max-w-xs truncate">{a.original_filename || a.filename}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-600">{a.notes || "—"}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500">
                      {fmtDate(a.uploaded_at)}<br />
                      <span className="text-[10px]">por {a.uploaded_by_name || "—"}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <Button size="sm" variant="ghost" onClick={() => onDownload(a)} title="Baixar" data-testid={`btn-attach-download-${idx}`}>
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => onDelete(a.id)} title="Excluir" data-testid={`btn-attach-delete-${idx}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

/** "Documentos" tab — upload form + list of patient personal attachments. */
export default function DocumentosTab({
  patient, docTypes, uploading,
  onUpload, onDownload, onDelete,
}) {
  return (
    <div className="space-y-4">
      <UploadCard docTypes={docTypes} onUpload={onUpload} uploading={uploading} />
      <AttachmentsTable
        attachments={patient.attachments || []}
        onDownload={onDownload}
        onDelete={onDelete}
      />
    </div>
  );
}
